// @flow
import React from 'react';
import { navigate } from '@reach/router';
import { cx, css } from 'emotion';
import { graphql } from 'gatsby';
import KeyHandler, { KEYPRESS } from 'react-key-handler';
import Layout from '../../components/Layout';
import Frontmatter from '../../components/Frontmatter';
import Timer from '../../components/Timer';
import { debounce, modes, buildSections, astToHtml } from './utils';
import { gamepadConnect, gamepadDisconnect } from './gamepad';
import slideReducer, { initialSlideState } from './reducer';
import s from './s.module.scss';

export const selector = `article[data-mode="presentation"] section, article[data-mode="speaker"] section, article[data-mode="notes"] section`;

// $FlowIgnoreNextLine GraphQL
const TalkTemplate = ({
  location,
  data: {
    site: {
      siteMetadata: { titleTemplate, twitter },
    },
    markdownRemark: {
      frontmatter: { styles, presentable, displayIntroSlides, ...frontmatter },
      fields: { slug },
      htmlAst,
    },
  },
}) => {
  const [state, dispatch] = React.useReducer(slideReducer, initialSlideState);
  const { mode, timer, started, slide, pages } = state;

  // when the slide updates, tell local storage and navigate to that slide
  React.useEffect(() => {
    navigate(`${window.location.pathname}${window.location.search}#${slide}`);
    if (typeof window !== 'undefined')
      window.localStorage.setItem(slug, window.location.hash.slice(1));
  }, [slide, slug]);

  // then we also need to actually scroll that into view
  React.useEffect(() => {
    if (pages && pages.length)
      location.hash &&
        pages[location.hash.slice(1)].scrollIntoView({
          behavior: 'smooth',
        });
  }, [location.hash, pages]);

  const syncSlideHandler = React.useCallback(
    ({ key, newValue }) => {
      if (key === slug) {
        dispatch({ type: 'to', to: newValue });
      }
    },
    [slug]
  );

  const toggleMode = React.useCallback(
    newMode => {
      dispatch({ type: 'toggle_mode', mode: newMode });
    },
    [dispatch]
  );

  // register sync slides events
  React.useEffect(() => {
    if ([modes.presentation, modes.speaker, modes.notes].includes(mode)) {
      window.addEventListener('storage', syncSlideHandler);
    } else {
      window.removeEventListener('storage', syncSlideHandler);
    }
  }, [mode]);

  // full screen
  // ref bound to layout with React.forwardRef
  const pageEl = React.useRef(null);
  const toggleFullscreen = React.useCallback(() => {
    if (pageEl.current) {
      if (!document.fullscreenElement) {
        typeof pageEl.current.requestFullscreen === 'function' &&
          pageEl.current.requestFullscreen().catch(err => {
            console.error(
              `Error attempting to enable full-screen mode: ${err.message} (${
                err.name
              })`
            );
          });
      } else {
        document.exitFullscreen();
      }
    }
  }, [pageEl]);

  // register joystick events
  // displayRef is used for gamepad to render directly
  // we _could_ put everything in React's cycle but it costs performance
  const pointerRef = React.useRef(null);
  const displayRef = React.useRef(null);
  React.useEffect(() => {
    const debouncedSlideTo = (target: 'prev' | 'next') =>
      debounce(() => {
        // $FlowFixMe
        dispatch({
          type: target,
        });
      }, 300);

    window.addEventListener(
      'gamepadconnected',
      gamepadConnect({
        x: debounce(() => toggleMode(modes.presentation), 300),
        y: debounce(() => toggleMode(modes.notes), 300),
        a: debounce(() => toggleMode(modes.article), 300),
        b: debounce(() => toggleMode(modes.speaker), 300),
        // plus is on the RHS, minus on the LHS
        plus: debounce(toggleTimer, 300),
        minus: debounce(toggleTimer, 300),
        home: debounce(() => dispatch({ type: 'to', to: 0 }), 300),
        trigger: debouncedSlideTo('next'),
        bumper: debouncedSlideTo('prev'),
        // used to write directly to the DOM
        // the joystick becomes janky if go through React
        pointerRef,
        displayRef,
      })
    );
    window.addEventListener('gamepaddisconnected', gamepadDisconnect);
  }, [pointerRef, displayRef]);

  // create timer variables to passed down to timer
  // put it here because we don't want switching mode to reset timer
  // plus, code is copied from Dan Abramov's example, thanks Dan!
  // https://codesandbox.io/s/q3181xz1pj
  const reset = React.useCallback(() => {
    dispatch({ type: 'reset_timer' });
  }, []);
  const toggleTimer = React.useCallback(() => {
    dispatch({ type: 'toggle_timer' });
  }, [dispatch]);
  React.useEffect(() => {
    if (started) {
      const id = setInterval(() => {
        dispatch({ type: 'tick' });
      }, 1000);
      return () => clearInterval(id);
    }
  }, [started]);

  // need to use a callback ref here because we want the page to rerender once the pages land
  const setPagesRef = React.useCallback(() => {
    [modes.presentation, modes.speaker, modes.notes].includes(mode) &&
      dispatch({
        type: 'set_pages',
        pages: document.querySelectorAll(selector),
      });
  }, [mode]);

  // custom styling
  const { googleFonts, variables } = styles || {};
  const googleFontsString = googleFonts
    ? `@import url('https://fonts.googleapis.com/css?family=${googleFonts
        .map(
          ({ name, styles }) =>
            `${name.split(' ').join('+')}${
              styles && styles.length ? ':' + styles.join(',') : ''
            }`
        )
        .join('|')}');`
    : '';
  const variablesString =
    variables && variables.length
      ? `
    ${variables.map(({ name, value }) => `${name}: ${value};`).join('')}
    `
      : '';

  const customStyles = googleFontsString + variablesString;
  const [maintitle, subtitle] = frontmatter.title.split('–');
  const sections = buildSections(htmlAst.children);
  const presenting = [modes.presentation, modes.speaker, modes.notes].includes(
    mode
  );
  return (
    <Layout
      ref={pageEl}
      location={location}
      {...{
        title: frontmatter.title,
        titleTemplate,
        description: `A talk by Wei @${twitter}: ${frontmatter.intro}`,
        permalink: `/talks${slug}`,
      }}
      className={cx(
        mode === modes.article && 'max-width-40-rem',
        s.container,
        css`
          ${customStyles}
        `
      )}
    >
      <div
        ref={pointerRef}
        className={css`
          display: ${mode === modes.article ? 'none' : 'block'};
          position: fixed;
          bottom: 1rem;
          right: 1rem;
          background: var(--presentation-color-accent);
          width: 5rem;
          height: 5rem;
          border-radius: 50%;
          opacity: 0.2;
          z-index: 1;
        `}
      />
      {presentable && (
        <>
          <KeyHandler
            keyEventName={KEYPRESS}
            keyValue="p"
            onKeyHandle={() => toggleMode(modes.presentation)}
          />
          <KeyHandler
            keyEventName={KEYPRESS}
            keyValue="s"
            onKeyHandle={() => toggleMode(modes.speaker)}
          />
          <KeyHandler
            keyEventName={KEYPRESS}
            keyValue="a"
            onKeyHandle={() => toggleMode(modes.article)}
          />
          <KeyHandler
            keyEventName={KEYPRESS}
            keyValue="n"
            onKeyHandle={() => toggleMode(modes.notes)}
          />
          {presenting && (
            <>
              <KeyHandler
                keyEventName={KEYPRESS}
                keyValue="f"
                onKeyHandle={toggleFullscreen}
              />
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(to => (
                <KeyHandler
                  key={`key-handler-${to}`}
                  keyEventName={KEYPRESS}
                  keyValue={`${to}`}
                  onKeyHandle={() =>
                    dispatch({
                      type: 'to',
                      to,
                    })
                  }
                />
              ))}
              <KeyHandler
                keyEventName={KEYPRESS}
                keyValue="k"
                onKeyHandle={() =>
                  dispatch({
                    type: 'prev',
                  })
                }
              />
              <KeyHandler
                keyEventName={KEYPRESS}
                keyValue="j"
                onKeyHandle={() => {
                  dispatch({
                    type: 'next',
                  });
                }}
              />
              <KeyHandler
                keyEventName={KEYPRESS}
                keyValue=" "
                onKeyHandle={() =>
                  dispatch({
                    type: 'next',
                  })
                }
              />
              <KeyHandler
                keyEventName={KEYPRESS}
                keyValue="b"
                onKeyHandle={() =>
                  dispatch({
                    type: 'next',
                  })
                }
              />
            </>
          )}
        </>
      )}

      {mode === modes.article && (
        <Frontmatter {...frontmatter}>
          {presentable && (
            <>
              <p>
                <strong>This page is playable as slides!</strong>
              </p>
              <em>
                Press <kbd>p</kbd> to play as slides, then <kbd>j</kbd> and{' '}
                <kbd>k</kbd> to page.
              </em>
              <hr />
            </>
          )}
        </Frontmatter>
      )}
      <article
        data-mode={mode}
        className={cx(
          'line-height-1-dot-4',
          presenting ? s.presenting : s.reading,
          s[mode]
        )}
        ref={setPagesRef}
      >
        {presenting && displayIntroSlides && (
          <>
            <section>
              <h1>{maintitle}</h1>
              {subtitle && <h2>-{subtitle}</h2>}
            </section>
            <section>
              {frontmatter.where && <h2>Hello, {frontmatter.where.name}!</h2>}
              <ul>
                <li>I'm Wei</li>
                <li>
                  React web developer at <a href="https://shopee.sg/">Shopee</a>
                </li>
              </ul>
            </section>
          </>
        )}
        {astToHtml({ type: 'root', children: sections })}
        {[modes.notes, modes.speaker].includes(mode) && (
          <Timer {...{ reset, toggleTimer, timer, started }} />
        )}
      </article>
      <div ref={displayRef} className={s.joyconStatus} />
    </Layout>
  );
};

export default TalkTemplate;

export const pageQuery = graphql`
  query TalkQuery($slug: String!) {
    site {
      siteMetadata {
        twitter
        titleTemplate
      }
    }
    markdownRemark(fields: { slug: { eq: $slug } }) {
      frontmatter {
        presentable
        slides
        title
        video
        post
        when
        intro
        where {
          link
          name
        }
        resources {
          link
          name
        }
        styles {
          googleFonts {
            name
            styles
          }
          variables {
            name
            value
          }
        }
        displayIntroSlides
      }
      htmlAst
      fields {
        slug
      }
    }
  }
`;
